import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, BadgeCheck, ExternalLink, Phone, Minus, Plus } from "lucide-react";
import { API, PAYMENTS } from "@/config/site";

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#E60000] outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-[#555] transition-colors";

export default function CheckoutModal({ product, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    pack_label: product.pack_options[0].label,
    quantity: 1,
    payment_method: "cashapp",
  });
  const [order, setOrder] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [stock, setStock] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/stock`)
      .then((r) => setStock(r.data))
      .catch(() => {});
  }, []);

  const remainingFor = (label) => {
    const s = stock?.find((x) => x.product_id === product.id);
    const p = s?.packs.find((x) => x.label === label);
    return p ? p.remaining : null;
  };

  const pack = product.pack_options.find((p) => p.label === form.pack_label);
  const packCap = remainingFor(form.pack_label) ?? pack.max;
  const stockEntry = stock?.find((x) => x.product_id === product.id);
  const dealPrice = stockEntry?.deal_price ?? product.deal_price;
  const total = dealPrice * form.quantity;
  const totalPairs = pack.pairs * form.quantity;
  const method = PAYMENTS.find((p) => p.id === form.payment_method);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const placeOrder = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.address.trim() || !form.city.trim() || !form.state.trim() || !form.zip.trim()) {
      setError("Fill in every field - name, email, street address, city, state and zip code.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/orders`, { ...form, product_id: product.id });
      setOrder(data);
      if (data.email_sent) toast.success(`Verification code sent to ${form.email}`);
      else toast.error("Order saved, but the email failed. Hit resend on the last step.");
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not place the order. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (code.trim().length !== 6) {
      setError("Enter the 6-character code from your email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/orders/verify`, {
        order_id: order.order_id,
        email: form.email,
        code: code.trim(),
      });
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.detail || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/orders/${order.order_id}/resend-code`, { email: form.email });
      toast.success("Code resent - check your inbox.");
    } catch {
      toast.error("Could not resend right now. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  const payWithCard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        order_id: order.order_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Card checkout unavailable right now - try another method.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        data-testid="checkout-modal"
        className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#121212] border border-[#262626]"
      >
        <div className="flex items-start justify-between p-6 border-b border-[#262626]">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#E60000] uppercase">
              Step {Math.min(step, 3)} of 3
            </p>
            <h3 className="font-display uppercase text-2xl text-white mt-1">{product.name}</h3>
            <p className="font-mono text-xs text-[#888] mt-1">
              <span className="line-through mr-2">${product.original_price}</span>$
              {dealPrice} per pack + shipping
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="close-checkout-btn"
            aria-label="Close checkout"
            className="text-[#888] hover:text-white transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#888] uppercase mb-2">
                  Pack option
                </p>
                <div className="space-y-2">
                  {product.pack_options.map((opt, i) => {
                    const rem = remainingFor(opt.label);
                    const gone = rem === 0;
                    return (
                      <button
                        key={opt.label}
                        disabled={gone}
                        onClick={() => {
                          set("pack_label", opt.label);
                          set("quantity", 1);
                        }}
                        data-testid={`pack-option-${i}`}
                        className={`w-full text-left border px-4 py-3 font-mono text-xs tracking-[0.1em] uppercase transition-colors ${
                          gone
                            ? "border-[#1a1a1a] text-[#444] cursor-not-allowed line-through"
                            : form.pack_label === opt.label
                              ? "border-[#E60000] text-[#E60000] bg-[#E60000]/10"
                              : "border-[#262626] text-[#999] hover:border-[#444]"
                        }`}
                      >
                        {opt.label} - {opt.pairs} pairs per pack -{" "}
                        {gone ? "SOLD OUT" : `${rem ?? opt.max} left`}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between border border-[#262626] px-4 py-3">
                <span className="font-mono text-xs tracking-[0.2em] text-[#888] uppercase">
                  Packs
                </span>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => set("quantity", Math.max(1, form.quantity - 1))}
                    data-testid="qty-minus-btn"
                    aria-label="Decrease quantity"
                    className="text-white hover:text-[#E60000] transition-colors"
                  >
                    <Minus size={16} />
                  </button>
                  <span data-testid="qty-value" className="font-display text-xl text-white w-6 text-center">
                    {form.quantity}
                  </span>
                  <button
                    onClick={() => set("quantity", Math.min(packCap, form.quantity + 1))}
                    data-testid="qty-plus-btn"
                    aria-label="Increase quantity"
                    className="text-white hover:text-[#E60000] transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              <p
                data-testid="order-pair-summary"
                className="font-mono text-[11px] tracking-[0.15em] text-[#E60000] uppercase"
              >
                {form.quantity} pack{form.quantity > 1 ? "s" : ""} x {pack.pairs} pairs ={" "}
                {totalPairs} pairs total
              </p>
              <input
                data-testid="checkout-name-input"
                className={inputCls}
                placeholder="FULL NAME"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
              <input
                data-testid="checkout-email-input"
                type="email"
                className={inputCls}
                placeholder="EMAIL (verification code goes here)"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
              <input
                data-testid="checkout-address-input"
                className={inputCls}
                placeholder="STREET ADDRESS (we ship here)"
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  data-testid="checkout-city-input"
                  className={inputCls}
                  placeholder="CITY"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                />
                <input
                  data-testid="checkout-state-input"
                  className={inputCls}
                  placeholder="STATE"
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                />
                <input
                  data-testid="checkout-zip-input"
                  className={inputCls}
                  placeholder="ZIP CODE"
                  value={form.zip}
                  onChange={(e) => set("zip", e.target.value)}
                />
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#888] uppercase mb-2">
                  Payment method
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENTS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => set("payment_method", m.id)}
                      data-testid={`payment-method-${m.id}`}
                      className={`border px-3 py-3 font-mono text-xs tracking-[0.1em] uppercase transition-colors ${
                        form.payment_method === m.id
                          ? "border-[#E60000] text-[#E60000] bg-[#E60000]/10"
                          : "border-[#262626] text-[#999] hover:border-[#444]"
                      }`}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
              {error && (
                <p data-testid="checkout-error" className="font-mono text-xs text-[#E60000]">
                  {error}
                </p>
              )}
              <button
                onClick={placeOrder}
                disabled={busy || packCap === 0}
                data-testid="place-order-btn"
                className="w-full bg-[#E60000] text-white font-display uppercase tracking-wide text-lg py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
              >
                {busy ? "Locking it in..." : `Place order - $${total}`}
              </button>
            </div>
          )}

          {step === 2 && order && (
            <div className="space-y-4">
              <div className="border border-[#E60000]/40 bg-[#E60000]/5 px-4 py-4">
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#E60000] uppercase">
                  Code sent to {form.email}
                </p>
                <p className="font-mono text-sm text-white mt-2">
                  {order.quantity} x {order.pack_label} ({order.total_pairs} pairs) - Total due:{" "}
                  <span className="font-display text-2xl">${order.total}</span>
                </p>
              </div>
              <p className="font-mono text-xs text-[#999] leading-relaxed">{method.note}</p>
              {method.qr && (
                <div className="border border-[#262626] bg-white p-4 max-w-[240px] mx-auto w-full">
                  <img
                    src={method.qr}
                    alt="PayPal QR code - scan to pay isaac brenna"
                    data-testid="checkout-paypal-qr"
                    className="w-full"
                  />
                  <p className="font-mono text-[10px] tracking-[0.15em] text-black text-center mt-2 uppercase">
                    Scan to pay isaac brenna
                  </p>
                </div>
              )}
              {method.id === "stripe" ? (
                <button
                  onClick={payWithCard}
                  disabled={busy}
                  data-testid="stripe-pay-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#E60000] text-white font-display uppercase tracking-wide text-lg py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
                >
                  {busy ? "Redirecting to Stripe..." : `Pay $${order.total} by card`}
                </button>
              ) : method.url ? (
                <a
                  href={method.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="payment-link-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-display uppercase tracking-wide text-lg py-4 hover:bg-[#E60000] hover:text-white transition-colors"
                >
                  Pay with {method.name} <ExternalLink size={18} />
                </a>
              ) : method.qr ? null : (
                <div
                  data-testid="visa-phone-number"
                  className="w-full inline-flex items-center justify-center gap-2 border border-[#333] text-white font-display uppercase tracking-wide text-lg py-4"
                >
                  <Phone size={18} /> {method.phone}
                </div>
              )}
              {method.id !== "stripe" && (
                <button
                  onClick={() => setStep(3)}
                  data-testid="paid-continue-btn"
                  className="w-full border border-[#333] text-white font-mono text-xs tracking-[0.2em] uppercase py-4 hover:border-[#E60000] hover:text-[#E60000] transition-colors"
                >
                  I've sent my payment - continue
                </button>
              )}
            </div>
          )}

          {step === 3 && order && (
            <div className="space-y-4">
              <p className="font-mono text-xs text-[#999] leading-relaxed">
                Enter the 6-character code we emailed to{" "}
                <span className="text-white">{form.email}</span> to verify your payment.
              </p>
              <input
                data-testid="verify-code-input"
                className={`${inputCls} text-center text-2xl tracking-[0.5em] uppercase`}
                placeholder="XXXXXX"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
              {error && (
                <p data-testid="verify-error" className="font-mono text-xs text-[#E60000]">
                  {error}
                </p>
              )}
              <button
                onClick={verify}
                disabled={busy}
                data-testid="verify-code-btn"
                className="w-full bg-[#E60000] text-white font-display uppercase tracking-wide text-lg py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
              >
                {busy ? "Verifying..." : "Verify my payment"}
              </button>
              <button
                onClick={resend}
                disabled={busy}
                data-testid="resend-code-btn"
                className="w-full font-mono text-[11px] tracking-[0.2em] uppercase text-[#888] hover:text-white transition-colors py-2"
              >
                Didn't get it? Resend code
              </button>
            </div>
          )}

          {step === 4 && (
            <div data-testid="order-success" className="text-center py-6 space-y-4">
              <BadgeCheck size={64} className="mx-auto text-[#E60000]" />
              <h4 className="font-display uppercase text-3xl text-white">Order verified</h4>
              <p className="font-mono text-xs text-[#999] leading-relaxed max-w-xs mx-auto">
                Payment matched to <span className="text-white">{form.email}</span>. We ship once
                your {method.name} payment lands. Screenshot this page.
              </p>
              <p
                data-testid="success-ship-to"
                className="font-mono text-[11px] text-[#E60000] leading-relaxed max-w-xs mx-auto uppercase tracking-[0.1em]"
              >
                Shipping to: {form.address}, {form.city}, {form.state} {form.zip}
              </p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#666] uppercase">
                Order ID: {order.order_id}
              </p>
              <button
                onClick={onClose}
                data-testid="success-close-btn"
                className="w-full bg-white text-black font-display uppercase tracking-wide text-lg py-4 hover:bg-[#E60000] hover:text-white transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
